import React, { useState, useEffect } from "react";
import axios from "axios";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";

import OuterLink from "src/app/components/OuterLink/OuterLink";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { translateMessage } from "src/app/methods/translateMessage";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import Loader from "src/app/components/Loader/Loader";
import { useLocation } from "react-router";
import { isEmailValid } from "src/utils/methods";
import { showToast } from "src/app/methods/showToast";
import { ReactComponent as WarningIcon } from "../../../images/warning-circle.svg";
import { Button } from "../../components/Button/Button";
import CustomInput, {
  CustomInputLabel,
} from "../../components/CustomInput/CustomInput";
import { API_URLS } from "../../../utils/API_URLS";
import { EventHelper } from "../../../helpers/EventHelper";
import {
  getCountryOptions,
  getPhoneNumberPrefixBaseOnCountryCode,
  formatPhoneNumber,
} from "./utils";
import { TrialRegistrationSuccess } from "./TrialRegistrationSuccess";

const countryOptions = getCountryOptions();

function TrialFormContent({ showForm, setShowForm, planName }) {
  const { search } = useLocation();
  const [person, setPerson] = useState({
    name: "",
    contactNumber: "",
    companyWebsite: "",
    market: [],
    email: "",
    useSoftwareAs: { label: "", value: "" },
    phoneNumberPrefix: "",
  });

  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailExistsError, setEmailExistsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [country, setCountry] = useState({
    label: "",
    value: "",
    code: "",
  });
  const [bdMemberData, setBdMemberData] = useState([]);

  const intl = useIntl();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEmailValid(person.email)) {
      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id="ws_incorrect_email_syntax"
          defaultMessage="Incorrect email syntax"
        />,
      );
      return;
    }

    const data = {
      wsTrialName: person.name,
      wsTrialContactNumber: `${person.phoneNumberPrefix} ${person.contactNumber}`,
      wsTrialCompanyWebsite: person.companyWebsite,
      wsTrialEmail: person.email,
      wsTrialUseSoftwareAs: person.useSoftwareAs.value,
      wsTrialCountryCode: country.code,
      wsTrialUtms: window?.localStorage?.getItem("idhUtms") ?? "",
      wsSubscriptionPlanName: planName,
    };

    try {
      setIsLoading(true);
      await axios.post(API_URLS.trialAccount, data).then((res) => {
        setBdMemberData(res.data.content.registerConfirmationData);
        setShowForm(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    } catch (err) {
      setShowForm(true);
      console.error(err);
      if (err.response.data.statusCode !== 200) {
        setEmailErrorMessage(
          err.response.data.errors[0]?.message || "Incorrect email",
        );
        if (err.response.data.statusCode === 409) {
          setEmailExistsError(true);
        }
      }
    } finally {
      setIsLoading(false);
    }

    EventHelper.emitEvent("ws:submitted.trial-registration-form");
  };

  const handleChange = (e) => {
    let newValue = e.target.value;

    if (e.target.name === "contactNumber") {
      if (person.phoneNumberPrefix) {
        newValue = newValue.slice(person.phoneNumberPrefix.length).trim();
      }
      newValue = formatPhoneNumber(newValue);
    }

    setPerson((prev) => ({
      ...prev,
      [e.target.name]: newValue,
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    const paramFccId = params.get("fccId");
    if (paramFccId) {
      const formData = new FormData();
      formData.append("userUuid", paramFccId);

      axios
        .post(API_URLS.credibilityRefererAccount, formData)
        .then((response) => {
          if (response.data?.status_code === 200) {
            const { userData } = response.data.content;
            let currentPhoneNumber = userData.phone;

            const currentCountry = countryOptions.find(
              (c) => c.code === userData.country,
            );

            if (currentCountry) {
              const currentCountryPhoneNumberPrefix =
                getPhoneNumberPrefixBaseOnCountryCode(userData.country);

              currentPhoneNumber = userData.phone.replace(
                currentCountryPhoneNumberPrefix,
                "",
              );
            }

            setPerson((prev) => ({
              ...prev,
              name: userData.name,
              email: userData.contactEmail,
              companyWebsite: userData.website,
              contactNumber: currentPhoneNumber,
              yourPosition: userData.position,
            }));
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [search]);

  const isPlanFree = planName === "lite";

  return (
    <div className="trial-view-content-right">
      <div className="trial-view-content-right__wrapper">
        <div
          className={classNames("trial-view-content-right__container", {
            "trial-view-content-right__container--confirm": !showForm,
          })}
        >
          {showForm ? (
            <form onSubmit={handleSubmit}>
              <CustomInputLabel aboveInput htmlFor="name">
                <IDHFormattedMessage id="ws_name" defaultMessage="Name" />
              </CustomInputLabel>
              <CustomInput
                name="name"
                id="name"
                value={person.name}
                onChange={handleChange}
                required
                type="text"
                placeholder="Jane Doe"
                fullWidth
              />

              <CustomInputLabel htmlFor="email" aboveInput>
                <IDHFormattedMessage
                  id="ws_business_email"
                  defaultMessage="Business Email"
                />
              </CustomInputLabel>
              <CustomInput
                name="email"
                id="email"
                value={person.email}
                onChange={handleChange}
                required
                type="email"
                placeholder="jane.doe@company.com"
                fullWidth
              />

              {isPlanFree && (
                <>
                  <CustomInputLabel aboveInput>
                    <IDHFormattedMessage
                      id="ws_country"
                      defaultMessage="Country"
                    />
                  </CustomInputLabel>
                  <CustomSelect
                    id="ws-country-code"
                    value={country}
                    options={countryOptions}
                    placeholder={`${translateMessage({
                      intl,
                      id: "ws_select_country",
                      defaultMessage: "Select country",
                    })}...`}
                    onChange={setCountry}
                    isSearchable
                    required
                  />
                </>
              )}

              {emailErrorMessage && (
                <>
                  <div className="trial-view-content-right__invalid-email-error">
                    <WarningIcon className="login-page-right__invalid-email-error-icon" />
                    <span
                      dangerouslySetInnerHTML={{ __html: emailErrorMessage }}
                    />
                    &nbsp;
                  </div>
                  {emailExistsError && (
                    <div className="trial-view-content-right__sign-in">
                      <span>
                        <Link to="/workspace">
                          <IDHFormattedMessage
                            id="ws_sign_in"
                            defaultMessage="Sign in"
                          />
                        </Link>
                        &nbsp;
                        <IDHFormattedMessage
                          id="ws_instead"
                          defaultMessage="instead"
                        />
                        .
                      </span>
                    </div>
                  )}
                </>
              )}

              <div className="trial-view-content-right__disclaimer">
                <IDHFormattedMessage
                  id="ws_by_signing_up_you_agree_with"
                  defaultMessage="By signing up, you agree with our"
                />
                <OuterLink href="https://docs.indahash.com/doc/?n=privacy-policy">
                  &nbsp;
                  <IDHFormattedMessage
                    id="ws_privacy_policy"
                    defaultMessage="Privacy Policy"
                  />
                </OuterLink>
                &nbsp;
                <IDHFormattedMessage id="ws_and" defaultMessage="and" />
                &nbsp;
                <OuterLink href="https://docs.indahash.com/doc/?n=terms-conditions-for-self-serve">
                  <IDHFormattedMessage
                    id="ws_terms_of_service"
                    defaultMessage="Terms of Service"
                  />
                </OuterLink>
              </div>
              <div className="trial-view-content-right__submit-wrapper">
                {isLoading ? (
                  <Loader />
                ) : (
                  <Button
                    size="large"
                    variant="blue"
                    className="trial-view-content-right__btn"
                  >
                    <IDHFormattedMessage
                      id="ws_continue"
                      defaultMessage="Continue"
                    />
                  </Button>
                )}
              </div>
            </form>
          ) : (
            <TrialRegistrationSuccess
              setShowForm={setShowForm}
              bdMemberData={bdMemberData}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export { TrialFormContent };
